import { useQuery } from '@tanstack/react-query';
import { AlertTriangle, ArrowDownRight, ArrowUpRight, FilePlus2, FileText, Receipt } from 'lucide-react';
import { useState } from 'react';
import { Link } from 'react-router';
import { Badge, Button, Card, Empty, Loading, PageHeader, StatusBadge, Table, Td, Th } from '../components/ui';
import { get } from '../lib/api';
import { useAuth } from '../lib/auth';
import { date, docLabel, mt } from '../lib/format';
import { TodayCard } from './automation';

interface Dash {
  today: string;
  thisMonth: { net: string | null; tax: string | null; gross: string | null; count: number };
  lastMonth: { gross: string | null };
  receivable: string;
  overdue: { v: string; n: number };
  monthly: { month: string; gross: string; tax: string }[];
  top: { id: string; name: string; gross: string }[];
  drafts: number;
  recent: { id: string; doc_type: string; full_number: string; issue_date: string; gross_total: string; status: string; customer_name: string }[];
}

const MONTHS = ['Jan', 'Fev', 'Mar', 'Abr', 'Mai', 'Jun', 'Jul', 'Ago', 'Set', 'Out', 'Nov', 'Dez'];

function Stat({ label, value, sub, tone }: { label: string; value: string; sub?: React.ReactNode; tone?: 'warn' }) {
  return (
    <div className="surface rounded-xl p-5 shadow-sm">
      <div className="text-sm text-muted">{label}</div>
      <div className={`mt-1 text-2xl font-bold tracking-tight num text-left ${tone === 'warn' ? 'text-red-700 dark:text-red-400' : ''}`}>{value}</div>
      {sub && <div className="mt-1 text-xs text-muted">{sub}</div>}
    </div>
  );
}

/**
 * Vendas dos últimos 12 meses — série única: uma cor (--chart-1: #a6070c claro / #ec5b5b escuro,
 * validadas contra a superfície de cada tema), sem legenda, barras finas com topo arredondado assente na
 * base, grelha recessiva, tooltip por barra e tabela equivalente.
 */
function MonthlyChart({ data, today }: { data: Dash['monthly']; today: string }) {
  const [hover, setHover] = useState<number | null>(null);
  const [y, m] = today.split('-').map(Number) as [number, number];
  const months = Array.from({ length: 12 }, (_, i) => {
    const d = new Date(Date.UTC(y, m - 12 + i, 1));
    const key = d.toISOString().slice(0, 7);
    return { key, label: MONTHS[d.getUTCMonth()]!, value: Number(data.find((x) => x.month === key)?.gross ?? 0) };
  });
  if (months.every((x) => x.value === 0)) {
    return <Empty title="Ainda sem vendas nos últimos 12 meses">O gráfico aparece com a primeira factura emitida.</Empty>;
  }
  const max = Math.max(1, ...months.map((x) => x.value));
  const nice = Math.pow(10, Math.floor(Math.log10(max)));
  const top = Math.ceil(max / nice) * nice;
  const W = 640, H = 220, padL = 52, padB = 24, padT = 10;
  const bw = (W - padL) / 12;
  const sy = (v: number) => padT + (H - padT - padB) * (1 - v / top);
  const ticks = [0, top / 2, top];
  const fmtK = (v: number) => (v >= 1e6 ? `${(v / 1e6).toFixed(1).replace('.', ',')} M` : v >= 1e3 ? `${Math.round(v / 1e3)} mil` : String(v));

  return (
    <div className="relative">
      <svg viewBox={`0 0 ${W} ${H}`} className="w-full" role="img" aria-label="Vendas com IVA nos últimos 12 meses">
        {ticks.map((t) => (
          <g key={t}>
            <line x1={padL} x2={W} y1={sy(t)} y2={sy(t)} stroke="var(--border)" strokeWidth={1} />
            <text x={padL - 8} y={sy(t) + 4} textAnchor="end" fontSize={11} fill="var(--muted)">{fmtK(t)}</text>
          </g>
        ))}
        {months.map((mo, i) => {
          const x = padL + i * bw + bw * 0.22;
          const w = bw * 0.56;
          const h = Math.max(0, sy(0) - sy(mo.value));
          const r = Math.min(4, h / 2, w / 2);
          const yTop = sy(mo.value);
          // topo arredondado (4px), base recta assente no eixo
          const path = h > 0 ? `M${x},${sy(0)} V${yTop + r} Q${x},${yTop} ${x + r},${yTop} H${x + w - r} Q${x + w},${yTop} ${x + w},${yTop + r} V${sy(0)} Z` : '';
          return (
            <g key={mo.key} onMouseEnter={() => setHover(i)} onMouseLeave={() => setHover(null)}>
              <rect x={padL + i * bw} y={padT} width={bw} height={H - padT - padB} fill="transparent" />
              {path && <path d={path} fill="var(--chart-1)" opacity={hover === null || hover === i ? 1 : 0.45} />}
              <text x={padL + i * bw + bw / 2} y={H - 6} textAnchor="middle" fontSize={11} fill="var(--muted)">{mo.label}</text>
            </g>
          );
        })}
      </svg>
      {hover !== null && (
        <div className="pointer-events-none absolute top-0 rounded-lg bg-gray-900 px-3 py-2 text-xs text-white shadow-lg dark:bg-white dark:text-gray-900"
          style={{ left: `${((padL + hover * bw + bw / 2) / W) * 100}%`, transform: 'translateX(-50%)' }}>
          <div className="font-semibold">{months[hover]!.key.split('-').reverse().join('/')}</div>
          <div>{mt(months[hover]!.value)} MT</div>
        </div>
      )}
      <details className="mt-2 text-sm">
        <summary className="cursor-pointer text-muted">Ver em tabela</summary>
        <Table>
          <thead><tr><Th>Mês</Th><Th className="text-right">Vendas c/ IVA (MT)</Th></tr></thead>
          <tbody>{months.map((mo) => <tr key={mo.key}><Td>{mo.key.split('-').reverse().join('/')}</Td><Td className="num">{mt(mo.value)}</Td></tr>)}</tbody>
        </Table>
      </details>
    </div>
  );
}

export function DashboardPage() {
  const { session, can } = useAuth();
  const q = useQuery({ queryKey: ['dashboard'], queryFn: () => get<Dash>('/reports/dashboard') });
  if (q.isLoading || !q.data) return <Loading />;
  const d = q.data;
  const cur = Number(d.thisMonth.gross ?? 0), prev = Number(d.lastMonth.gross ?? 0);
  const delta = prev > 0 ? ((cur - prev) / prev) * 100 : null;

  return (
    <>
      <PageHeader
        title={`Olá, ${session!.user.name.split(' ')[0]}`}
        subtitle={session!.tenant.legal_name}
        actions={can('documents:create') && <>
          <Link to="/documentos/novo?tipo=FR"><Button variant="secondary"><Receipt className="size-4" />Factura-Recibo</Button></Link>
          <Link to="/documentos/novo?tipo=FT"><Button><FilePlus2 className="size-4" />Nova factura</Button></Link>
        </>}
      />
      <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-4">
        <Stat label="Vendas do mês (c/ IVA)" value={`${mt(cur)} MT`} sub={delta === null ? `${d.thisMonth.count} documentos` : (
          <span className="inline-flex items-center gap-1">{delta >= 0 ? <ArrowUpRight className="size-3" /> : <ArrowDownRight className="size-3" />}{Math.abs(delta).toFixed(0)}% face ao mês anterior</span>
        )} />
        <Stat label="IVA liquidado no mês" value={`${mt(d.thisMonth.tax ?? 0)} MT`} sub="A entregar na declaração periódica" />
        <Stat label="A receber" value={`${mt(d.receivable)} MT`} sub="Facturas e notas de débito em aberto" />
        <Stat label="Vencido" tone={Number(d.overdue.v) > 0 ? 'warn' : undefined} value={`${mt(d.overdue.v)} MT`}
          sub={d.overdue.n ? <Link to="/contas-correntes" className="inline-flex items-center gap-1 underline"><AlertTriangle className="size-3" />{d.overdue.n} documento{d.overdue.n > 1 ? 's' : ''} em atraso</Link> : 'Nada em atraso'} />
      </div>

      <div className="mt-6"><TodayCard /></div>

      <div className="mt-6 grid gap-6 lg:grid-cols-3">
        <Card title="Vendas — últimos 12 meses" className="lg:col-span-2"><MonthlyChart data={d.monthly} today={d.today} /></Card>
        <Card title="Melhores clientes do ano">
          {d.top.length ? (
            <ol className="space-y-3">
              {d.top.map((c, i) => (
                <li key={c.id} className="flex items-center justify-between gap-3 text-sm">
                  <Link to={`/clientes/${c.id}`} className="min-w-0 truncate hover:underline"><span className="mr-2 text-muted">{i + 1}.</span>{c.name}</Link>
                  <span className="num font-medium">{mt(c.gross)}</span>
                </li>
              ))}
            </ol>
          ) : <Empty title="Sem vendas este ano" />}
        </Card>
      </div>

      <Card className="mt-6" title="Últimos documentos" actions={d.drafts > 0 && <Link to="/documentos?status=draft"><Badge tone="amber">{d.drafts} rascunho{d.drafts > 1 ? 's' : ''} por emitir</Badge></Link>}>
        {d.recent.length ? (
          <Table>
            <thead><tr><Th>Documento</Th><Th>Cliente</Th><Th>Data</Th><Th>Estado</Th><Th className="text-right">Total</Th></tr></thead>
            <tbody>
              {d.recent.map((r) => (
                <tr key={r.id} className="hover:bg-[var(--surface-2)]">
                  <Td><Link className="font-medium hover:underline" to={`/documentos/${r.id}`}>{r.full_number}</Link><div className="text-xs text-muted">{docLabel(r.doc_type)}</div></Td>
                  <Td>{r.customer_name}</Td><Td>{date(r.issue_date)}</Td><Td><StatusBadge status={r.status} /></Td>
                  <Td className="num">{mt(r.gross_total)}</Td>
                </tr>
              ))}
            </tbody>
          </Table>
        ) : (
          <Empty icon={<FileText className="size-6" />} title="Ainda não emitiu documentos">
            Comece pela primeira factura — demora menos de um minuto.
          </Empty>
        )}
      </Card>
    </>
  );
}
